import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '@/styles/helpGuideTour.css';
import { useAuthStore } from '@/stores/auth';
import { useHelpGuidesStore } from '@/stores/helpGuides';
import { getHelpGuideById } from '@/features/helpGuides/catalog';

const ROUTE_WAIT_TIMEOUT = 8000;
const ELEMENT_WAIT_TIMEOUT = 1800;
const POLL_INTERVAL = 120;
const TOUR_BOOT_DELAY = 220;
const MAX_STEP_RETRIES = 1;
const FIRST_STEP_WAIT_TIMEOUT = 9000;

async function waitForRoute(expectedPath: string, timeoutMs: number): Promise<boolean> {
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    const currentPathWithSearch = `${window.location.pathname}${window.location.search}`;
    const matches = expectedPath.includes('?')
      ? currentPathWithSearch === expectedPath
      : window.location.pathname === expectedPath;

    if (matches) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
  }

  return false;
}

async function waitForElement(selector: string, timeoutMs: number): Promise<Element | null> {
  if (!selector) return null;
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    let element: Element | null = null;
    try {
      element = document.querySelector(selector);
    } catch {
      return null;
    }
    if (element) {
      return element;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
  }

  return null;
}

async function waitForFrames(count = 2): Promise<void> {
  for (let i = 0; i < count; i += 1) {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }
}

function normalizeTourButtons() {
  const nextBtn = document.querySelector<HTMLButtonElement>(
    '.driver-popover .driver-popover-next-btn',
  );
  const doneBtn = document.querySelector<HTMLButtonElement>(
    '.driver-popover .driver-popover-done-btn',
  );

  [nextBtn, doneBtn].forEach((btn) => {
    if (!btn) return;
    btn.classList.remove('driver-popover-btn-disabled');
    btn.removeAttribute('disabled');
    btn.setAttribute('aria-disabled', 'false');
  });
}

export function HelpGuideTourRunner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const pendingGuideToStart = useHelpGuidesStore((state) => state.pendingGuideToStart);
  const pendingGuideRequestId = useHelpGuidesStore(
    (state) => state.pendingGuideRequestId,
  );
  const markGuideCompleted = useHelpGuidesStore((state) => state.markGuideCompleted);
  const clearPendingGuide = useHelpGuidesStore((state) => state.clearPendingGuide);

  const driverRef = useRef<Driver | null>(null);

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  const collectSteps = async (
    guide: NonNullable<ReturnType<typeof getHelpGuideById>>,
    tFn: typeof t,
  ): Promise<DriveStep[]> => {
    const buildFromDom = () =>
      guide.steps
        .map((step) => {
          try {
            return { step, found: document.querySelector(step.selector) };
          } catch {
            return { step, found: null };
          }
        })
        .filter((entry) => Boolean(entry.found))
        .map((entry) => ({
          element: entry.step.selector,
          popover: {
            title: tFn(entry.step.titleKey),
            description: tFn(entry.step.descriptionKey),
            side: entry.step.side,
          },
        }));

    const immediateSteps = buildFromDom();
    if (immediateSteps.length > 0) {
      return immediateSteps;
    }

    await waitForElement(guide.steps[0]?.selector ?? '', ELEMENT_WAIT_TIMEOUT);

    return buildFromDom()
      .map((step) => step)
      .filter(Boolean)
      .map((step) => step);
  };

  useEffect(() => {
    let cancelled = false;

    const runGuide = async () => {
      if (!pendingGuideToStart || !user?.id) return;

      const guide = getHelpGuideById(user, pendingGuideToStart);
      if (!guide) {
        clearPendingGuide();
        return;
      }

      const startPath = guide.startPath || guide.routePath;
      const currentPathWithSearch = `${window.location.pathname}${window.location.search}`;
      const shouldNavigate = startPath.includes('?')
        ? currentPathWithSearch !== startPath
        : window.location.pathname !== guide.routePath;

      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }

      if (shouldNavigate) {
        navigate(startPath);
      }

      const routeReady = await waitForRoute(startPath, ROUTE_WAIT_TIMEOUT);
      if (cancelled) {
        return;
      }
      if (!routeReady) {
        clearPendingGuide();
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, TOUR_BOOT_DELAY));
      await waitForFrames(2);

      const firstStepReady = await waitForElement(
        guide.steps[0]?.selector ?? '',
        FIRST_STEP_WAIT_TIMEOUT,
      );
      if (cancelled) {
        return;
      }
      if (!firstStepReady) {
        clearPendingGuide();
        return;
      }

      let steps: DriveStep[] = [];
      for (let attempt = 0; attempt < MAX_STEP_RETRIES; attempt += 1) {
        steps = await collectSteps(guide, t);
        if (steps.length > 0) break;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      if (cancelled) {
        return;
      }
      if (steps.length === 0) {
        clearPendingGuide();
        return;
      }

      let reachedLastStep = false;

      const driverObj = driver({
        showProgress: true,
        allowClose: true,
        overlayClickBehavior: () => {},
        disableActiveInteraction: true,
        overlayOpacity: 0.45,
        animate: true,
        popoverClass: 'help-tour-popover',
        nextBtnText: t('help_guides.tour.next'),
        prevBtnText: t('help_guides.tour.prev'),
        doneBtnText: t('help_guides.tour.done'),
        progressText: t('help_guides.tour.progress'),
        onHighlighted: (_element, _step, opts) => {
          reachedLastStep = opts.driver.isLastStep();
          requestAnimationFrame(() => normalizeTourButtons());
        },
        onPopoverRender: () => {
          normalizeTourButtons();
          window.setTimeout(() => normalizeTourButtons(), 80);
        },
        onDestroyed: () => {
          if (reachedLastStep) {
            markGuideCompleted(user.id, guide.id);
          }
          clearPendingGuide();
          driverRef.current = null;
        },
      });

      driverObj.setSteps(steps);
      driverRef.current = driverObj;
      driverObj.drive();
    };

    runGuide();

    return () => {
      cancelled = true;
    };
  }, [
    pendingGuideToStart,
    pendingGuideRequestId,
    user,
    navigate,
    t,
    markGuideCompleted,
    clearPendingGuide,
  ]);

  return null;
}
